import type { GatewayBrowserClient } from "../gateway.ts";
import type { UserRole } from "../storage.ts";

export type RoleSettingsMap = Record<string, Record<string, unknown>>;

export type RoleSettingsState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  roleSettingsLoading: boolean;
  roleSettingsMap: RoleSettingsMap;
  roleSettingsDirty: Set<string>;
  roleSettingsSaving: boolean;
  roleSettingsError: string | null;
};

export const ROLE_SETTINGS_EDITABLE_ROLES: UserRole[] = ["admin", "project_admin", "user"];

export const CHAT_SHOW_PICKER_KEY = "chat.showProjectPicker";

export function roleSettingsDirtyKey(role: string, key: string): string {
  return `${role}:${key}`;
}

export async function loadRoleSettings(state: RoleSettingsState) {
  if (!state.client || !state.connected) {
    return;
  }
  if (state.roleSettingsLoading) {
    return;
  }
  state.roleSettingsLoading = true;
  state.roleSettingsError = null;
  try {
    const res = await state.client.request<{ settings: RoleSettingsMap }>(
      "igloobot.settings.roleSettings.get",
      {},
    );
    state.roleSettingsMap = res.settings ?? {};
    state.roleSettingsDirty = new Set();
  } catch (err) {
    state.roleSettingsError = String(err);
  } finally {
    state.roleSettingsLoading = false;
  }
}

export async function saveRoleSettings(state: RoleSettingsState) {
  if (!state.client || !state.connected || state.roleSettingsSaving) {
    return;
  }
  if (state.roleSettingsDirty.size === 0) {
    return;
  }
  state.roleSettingsSaving = true;
  state.roleSettingsError = null;
  try {
    const entries = Array.from(state.roleSettingsDirty);
    for (const entry of entries) {
      const [role, key] = entry.split(":");
      const value = state.roleSettingsMap[role]?.[key];
      await state.client.request("igloobot.settings.roleSettings.set", {
        role,
        key,
        value,
      });
    }
    state.roleSettingsDirty = new Set();
  } catch (err) {
    state.roleSettingsError = String(err);
  } finally {
    state.roleSettingsSaving = false;
  }
}

export function setRoleSettingValue(
  state: RoleSettingsState,
  role: string,
  key: string,
  value: unknown,
) {
  if (!state.roleSettingsMap[role]) {
    state.roleSettingsMap[role] = {};
  }
  state.roleSettingsMap[role][key] = value;
  state.roleSettingsDirty.add(roleSettingsDirtyKey(role, key));
}

export function getRoleSettingValue(
  state: RoleSettingsState,
  role: string,
  key: string,
  defaultValue: unknown = true,
): unknown {
  return state.roleSettingsMap[role]?.[key] ?? defaultValue;
}
